import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { legacyApi } from '../../../services/legacyApi';
import { Plus, FileText, RotateCw, Trash2, Pencil } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { CompactTable } from '../../../components/ui/CompactTable';
import { useConfirm } from '../../../hooks/use-confirm';
import { toast } from 'sonner';
import { StandardActions } from '../components/StandardActions';
import { TableFilterBar } from '../../../components/table-filters/TableFilterBar';
import { useTableFilterState } from '../../../hooks/useTableFilterState';
import { FlowEntryBanner } from '../../../components/workflow/FlowEntryBanner';
interface Inquiry {
    inquiry_id: number;
    customer_id: number;
    customer_name?: string;
    project_scope?: string;
    project_description?: string;
    date_enquired?: string;
    inquiry_status_id?: number;
    // Enriched
    inquiry_number?: string;
    project_type_name?: string;
    status_name?: string;
    followup_date?: string;
}

export const InquiriesList: React.FC = () => {
    const PAGE_SIZE = 50;
    const navigate = useNavigate();
    const { confirm } = useConfirm();
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [debouncedQ, setDebouncedQ] = useState('');
    const { state, setFilter, clearAll, isDirty } = useTableFilterState({
        q: '',
        status: 'all',
        dateRange: '|'
    });

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQ(state.q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [state.q]);

    const loadInquiries = async (reset: boolean = true) => {
        const nextOffset = reset ? 0 : inquiries.length;
        if (reset) setLoading(true);
        else setLoadingMore(true);
        const [start, end] = state.dateRange.split('|');
        try {
            const response = await legacyApi.inquiries.listPaged({
                q: debouncedQ || undefined,
                status_id: state.status !== 'all' ? Number(state.status) : undefined,
                date_from: start || undefined,
                date_to: end || undefined,
                limit: PAGE_SIZE,
                offset: nextOffset,
            });
            setInquiries((prev) => (reset ? response.items : [...prev, ...response.items]));
            setTotalCount(response.total);
            setHasMore(response.has_more);
        } catch (error) {
            console.error('Failed to load inquiries:', error);
        } finally {
            if (reset) setLoading(false);
            else setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadInquiries(true);
    }, [debouncedQ, state.status, state.dateRange]);

    const handleConvertToEstimate = async (inquiryId: number) => {
        const ok = await confirm('Convert this inquiry to a new estimate?', { confirmText: 'Convert', cancelText: 'Cancel' });
        if (!ok) return;
        try {
            const result = await legacyApi.inquiries.convert(inquiryId);
            toast.success(`Estimate #${result.estimate_id} created successfully!`);
            navigate(`/legacy/estimates/${result.estimate_id}`);
        } catch (error) {
            console.error('Conversion failed:', error);
            toast.error('Failed to convert inquiry');
        }
    };

    const getStatusBadge = (statusId?: number) => {
        const statuses: Record<number, { label: string; color: string }> = {
            1: { label: 'Open', color: 'bg-blue-600' },
            2: { label: 'In Progress', color: 'bg-yellow-600' },
            3: { label: 'Quoted', color: 'bg-purple-600' },
            4: { label: 'Converted', color: 'bg-green-600' },
            5: { label: 'Lost', color: 'bg-red-600' }
        };

        const status = statuses[statusId || 1];
        return (
            <span className={`px-2 py-1 rounded text-xs ${status.color} text-white font-medium inline-block w-20 text-center`}>
                {status.label}
            </span>
        );
    };

    const columns = [
        { header: "Inquiry ID", accessor: (i: Inquiry) => i.inquiry_number || `HC/ENQ/${String(i.inquiry_id).padStart(4, '0')}`, className: 'font-mono whitespace-nowrap w-32' },
        { header: "Project Type", accessor: (i: Inquiry) => i.project_type_name || '-', className: 'whitespace-nowrap w-32' },
        { header: "Customer Name", accessor: (i: Inquiry) => i.customer_name || 'N/A', className: 'font-medium min-w-[200px] max-w-[350px] truncate' },
        { header: "Status", accessor: (i: Inquiry) => getStatusBadge(i.inquiry_status_id), className: 'w-24 text-center' },
        { header: "Followup Date", accessor: (i: Inquiry) => i.followup_date ? new Date(i.followup_date).toLocaleDateString() : '-', className: 'w-32 whitespace-nowrap' },
        {
            header: "Actions",
            accessor: (i: Inquiry) => (
                <StandardActions
                    edit={{
                        title: "Edit Inquiry",
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`/legacy/inquiries/${i.inquiry_id}`);
                        },
                        icon: <Pencil className="w-4 h-4 text-blue-600" />
                    }}
                    remove={{
                        title: "Delete Inquiry",
                        onClick: async (e) => {
                            e.stopPropagation();
                            const label = `HC/ENQ/${String(i.inquiry_id).padStart(4, '0')}`;
                            const ok = await confirm(`Delete ${label}?`, { variant: 'destructive', confirmText: 'Delete', cancelText: 'Cancel' });
                            if (!ok) return;
                            try {
                                await legacyApi.inquiries.delete(i.inquiry_id);
                                setInquiries(prev => prev.filter(x => x.inquiry_id !== i.inquiry_id));
                                setTotalCount(prev => Math.max(prev - 1, 0));
                                toast.success('Inquiry deleted');
                            } catch (err) {
                                toast.error('Failed to delete');
                            }
                        },
                        icon: <Trash2 className="w-4 h-4 text-red-600" />
                    }}
                    overflow={[
                        {
                            label: "Convert to Estimate",
                            onClick: (e) => {
                                e.stopPropagation();
                                handleConvertToEstimate(i.inquiry_id);
                            },
                            disabled: i.inquiry_status_id === 2,
                            icon: <FileText className="w-4 h-4 text-green-600" />
                        }
                    ]}
                />
            ),
            className: 'text-right'
        }
    ];

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-foreground">Inquiries</h1>
                    <p className="text-muted-foreground">Manage customer inquiries - showing {inquiries.length} of {totalCount}</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => loadInquiries(true)} title="Refresh data">
                        <RotateCw className="mr-2 h-4 w-4" /> Refresh
                    </Button>
                    <Button asChild>
                        <Link to="/legacy/inquiries/new">
                            <Plus className="mr-2 h-4 w-4" /> New Inquiry
                        </Link>
                    </Button>
                </div>
            </div>

            <div className="space-y-4">
                <FlowEntryBanner
                    title="Sales Path Guidance"
                    pathLabel="Inquiry -> Estimate -> SO"
                    nextHint="Qualify inquiry, then convert to estimate when scope and customer details are ready."
                />
                <div className="flex items-center gap-2">
                    <TableFilterBar
                        searchValue={state.q}
                        onSearchChange={(value) => setFilter('q', value)}
                        searchPlaceholder="Search by inquiry ID, customer, scope..."
                        controls={[
                            {
                                key: 'dateRange',
                                label: 'Date',
                                type: 'daterange',
                                value: state.dateRange,
                                onChange: (value) => setFilter('dateRange', value)
                            },
                            {
                                key: 'status',
                                label: 'Status',
                                type: 'select',
                                value: state.status,
                                onChange: (value) => setFilter('status', value),
                                options: [
                                    { value: 'all', label: 'All Statuses' },
                                    { value: '1', label: 'Open' },
                                    { value: '2', label: 'In Progress' },
                                    { value: '3', label: 'Quoted' },
                                    { value: '4', label: 'Converted' },
                                    { value: '5', label: 'Lost' }
                                ]
                            }
                        ]}
                        isDirty={isDirty}
                        onClear={clearAll}
                    />
                </div>

                <div className="min-h-[400px] overflow-x-auto">
                    {loading ? (
                        <div className="p-8 text-center text-muted-foreground">Loading Inquiries...</div>
                    ) : (
                        <>
                            <CompactTable
                                data={inquiries}
                                columns={columns}
                                keyField="inquiry_id"
                                onRowClick={(item) => navigate(`/legacy/inquiries/${item.inquiry_id}`)}
                            />
                            {hasMore && (
                                <div className="flex justify-center mt-4">
                                    <Button variant="ghost" onClick={() => loadInquiries(false)} disabled={loadingMore}>
                                        {loadingMore ? 'Loading...' : 'Load older inquiries'}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
